'use client'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'motion/react'

export const SLIDE_TIME = 5000

export const useSlider = (totalSlides: number, always = false) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const triggerRef = useRef(null)
  const tickRef = useRef<((next: number) => void) | null>(null)

  const inView = useInView(triggerRef, {
    margin: '0px',
    amount: 0 // equivalent to threshold: 0
  })

  const clearTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const tick = useCallback((next: number) => {
    clearTimer()

    const index = (next + totalSlides) % totalSlides

    setCurrentSlide(index)
    timeoutRef.current = setTimeout(() => {
      if (tickRef.current) {
        tickRef.current(index + 1)
      }
    }, SLIDE_TIME)
  }, [totalSlides])

  // Update ref whenever tick changes
  useEffect(() => {
    tickRef.current = tick
  }, [tick])

  const startAnimation = useCallback(() => {
    tick(currentSlide)
  }, [tick, currentSlide])

  const nextSlide = useCallback(() => {
    tick(currentSlide)
  }, [tick, currentSlide])

  const prevSlide = useCallback(() => {
    tick(currentSlide - 2)
  }, [tick, currentSlide])

  useEffect(() => {
    if (inView || always) {
      clearTimer()
      timeoutRef.current = setTimeout(() => startAnimation(), SLIDE_TIME)
    } else {
      clearTimer()
    }

    return clearTimer
  }, [inView, always, startAnimation])

  return {
    currentSlide,
    triggerRef,
    nextSlide,
    prevSlide,
    setSlide: setCurrentSlide,
    startAnimation
  }
}
